import { useState } from "react";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { Dialog, DialogContent } from "@component/dialog";
import { FileTextIcon } from "lucide-react";
import { Applicant } from "@type/applicant";

interface Props {
    isOpen: boolean;
    applicants: Applicant[];
    onClose: () => void;
    onSubmit: (note: string) => void;
}

export default function InternalOfferNoteDialog({
    isOpen,
    applicants = [],
    onClose,
    onSubmit,
}: Props) {
    const [note, setNote] = useState("");

    const handleSubmit = () => {
        onSubmit(note);
        setNote(""); // Reset note after submission
        onClose();
    };

    const handleClose = () => {
        setNote(""); // Reset note when closing
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-[600px] p-0 gap-0 flex flex-col">
                <div className="p-6">
                    {/* Header */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                                <FileTextIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    Mark as Offer Sent
                                </h2>
                                {applicants.length > 0 && (
                                    <p className="text-sm text-muted-foreground">
                                        Add internal note for {applicants.length > 1 ? `${applicants.length} applicants` : (
                                            <span className="capitalize">
                                                {applicants[0].applicant_name}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Internal Offer Note Field */}
                    <div className="space-y-4">
                        <div className="mb-2">
                            <Label
                                htmlFor="internal-offer-note"
                                className="text-sm font-medium text-foreground"
                            >
                                Internal Offer Note <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                id="internal-offer-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                className="mt-2 resize-none min-h-[120px] text-foreground placeholder:text-muted-foreground"
                                placeholder="Add internal notes about this offer (e.g., salary negotiation details, special terms, hiring manager approval, etc.)..."
                            />

                            <p className="text-xs text-muted-foreground mt-1.5">
                                This note is for internal use only and will not be sent to the
                                candidate.
                            </p>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="mt-6 flex justify-end gap-3">
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={note.trim() === ""}
                            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Mark as Offer Sent
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
